import { z } from '@faceup/validation'
import { EvidenceStatus, EvidenceType, InvestigationClosingReason } from '../enums'
import { Language } from '../localization'
import { tiptapContentSchema } from './tiptapContentSchema'

// Model ref schemas
export const memberRefSchema = z.object({ id: z.uuidv7(), name: z.string() }).brand('MemberRef')

export const employeeRefSchema = z.object({ id: z.uuidv7(), name: z.string() }).brand('EmployeeRef')

export const organizationalUnitRefSchema = z
  .object({ id: z.uuidv7(), name: z.string() })
  .brand('OrganizationalUnitRef')

export const categoryRefSchema = z
  .object({ id: z.uuidv7(), name: z.partialRecord(z.enum(Language), z.string()) })
  .brand('CategoryRef')

export const evidenceRefSchema = z.object({ id: z.uuidv7(), name: z.string() }).brand('EvidenceRef')

export const attachmentRefSchema = z
  .object({ id: z.uuidv7(), filename: z.string() })
  .brand('AttachmentRef')

// Base date schemas
export const dateSchema = z.iso.date()
export const dateRangeSchema = z.object({
  start: dateSchema,
  end: dateSchema.nullable(),
})

// Field change schema helpers
const fieldChangeSchema = <F extends z.util.Literal, S extends z.Schema>(fieldName: F, schema: S) =>
  z.object({ field: z.literal(fieldName), before: schema, after: schema })

// Field change schema for different entities
export const planFieldChangeSchema = z.discriminatedUnion('field', [
  fieldChangeSchema('organizationalUnit', organizationalUnitRefSchema),
  fieldChangeSchema('category', categoryRefSchema),
  fieldChangeSchema('startOfInvestigation', dateSchema.nullable()),
  fieldChangeSchema('incidentDateRange', dateRangeSchema.nullable()),
  fieldChangeSchema('dueDateOfInvestigation', dateSchema.nullable()),
  fieldChangeSchema('description', tiptapContentSchema.nullable()),
])
export const planFieldEnum = z.enum(planFieldChangeSchema.options.map(o => o.shape.field.value))

export const involvedPersonMetadataChangeSchema = z.discriminatedUnion('field', [
  fieldChangeSchema('role', z.string()),
  fieldChangeSchema('note', z.string().nullable()),
])
export const involvedPersonMetadataFieldEnum = z.enum(
  involvedPersonMetadataChangeSchema.options.map(o => o.shape.field.value)
)

export const evidenceMetadataChangeSchema = z.discriminatedUnion('field', [
  fieldChangeSchema('name', z.string()),
  fieldChangeSchema('type', z.enum(EvidenceType)),
  fieldChangeSchema('status', z.enum(EvidenceStatus).nullable()),
  fieldChangeSchema('description', z.string().nullable()),
  fieldChangeSchema('employee', employeeRefSchema.nullable()),
  fieldChangeSchema('attachment', attachmentRefSchema.nullable()),
])
export const evidenceMetadataFieldEnum = z.enum(
  evidenceMetadataChangeSchema.options.map(o => o.shape.field.value)
)

export const reportFieldChangeSchema = z.discriminatedUnion('field', [
  fieldChangeSchema('summary', tiptapContentSchema.nullable()),
  fieldChangeSchema('recommendations', tiptapContentSchema.nullable()),
  fieldChangeSchema('notes', tiptapContentSchema.nullable()),
  fieldChangeSchema('endDate', dateSchema.nullable()),
  fieldChangeSchema('closingReason', z.enum(InvestigationClosingReason).nullable()),
])
export const reportFieldEnum = z.enum(reportFieldChangeSchema.options.map(o => o.shape.field.value))

export const investigationAuditLogPayloadSchema = z.discriminatedUnion('type', [
  // Investigation
  z.object({
    type: z.literal('InvestigationInitiated'),
    investigators: z.array(memberRefSchema),
  }),
  z.object({ type: z.literal('InvestigationFirstChange') }),
  z.object({ type: z.literal('InvestigationRenamed'), before: z.string(), after: z.string() }),
  z.object({ type: z.literal('InvestigationClosed') }),
  // Moved from a legacy investigation, by a member or by FaceUp in the forced migration
  z.object({ type: z.literal('InvestigationMigratedFromLegacy') }),
  z.object({ type: z.literal('InvestigationMigratedFromLegacyBySystem') }),
  z.object({
    type: z.literal('AnalysisChanged'),
    before: tiptapContentSchema.nullable(),
    after: tiptapContentSchema.nullable(),
  }),

  // Investigation plan
  z.object({
    type: z.literal('PlanFieldChanged'),
    changes: z.array(planFieldChangeSchema),
  }),

  // Used when the category/organizational unit is deleted from settings
  z.object({
    type: z.literal('InvestigationPlanCategoryChangeDueToCategoryDeletion'),
    before: categoryRefSchema,
    after: categoryRefSchema,
  }),
  z.object({
    type: z.literal('InvestigationPlanOrgUnitChangeDueToOrgUnitDeletion'),
    before: organizationalUnitRefSchema,
    after: organizationalUnitRefSchema,
  }),

  // Investigators
  z.object({ type: z.literal('InvestigatorAdded'), member: memberRefSchema }),
  // Added server-side to resolve the last-investigator guard (member deletion / access revoke).
  z.object({
    type: z.literal('InvestigatorAddedForAccessPreservation'),
    member: memberRefSchema,
  }),
  z.object({ type: z.literal('InvestigatorRemoved'), member: memberRefSchema }),
  z.object({ type: z.literal('InvestigatorRemovedDueToMemberDeletion'), member: memberRefSchema }),
  // Written when an investigator loses access via an InvestigationAccess permission revoke. The
  // assignment row is kept (so re-enabling restores access), but the access loss is recorded.
  z.object({
    type: z.literal('InvestigatorRemovedDueToPermissionRevoke'),
    member: memberRefSchema,
  }),

  // Involved people
  z.object({
    type: z.literal('InvolvedPersonAdded'),
    employee: employeeRefSchema,
    metadata: z.object({
      role: z.string(),
      note: z.string().nullable(),
    }),
  }),
  z.object({
    type: z.literal('InvolvedPersonMetadataChanged'),
    employee: employeeRefSchema,
    changes: z.array(involvedPersonMetadataChangeSchema),
  }),
  z.object({ type: z.literal('InvolvedPersonRemoved'), employee: employeeRefSchema }),

  // Evidence
  z.object({
    type: z.literal('EvidenceCreated'),
    evidence: evidenceRefSchema,
    metadata: z.object({
      name: z.string(),
      type: z.enum(EvidenceType),
      status: z.enum(EvidenceStatus).nullable(),
      description: z.string().nullable(),
      employee: employeeRefSchema.nullable(),
      attachment: attachmentRefSchema.nullable(),
    }),
  }),
  z.object({
    type: z.literal('EvidenceMetadataChanged'),
    evidence: evidenceRefSchema,
    changes: z.array(evidenceMetadataChangeSchema),
  }),
  z.object({
    type: z.literal('EvidenceDeleted'),
    evidence: evidenceRefSchema,
  }),
  z.object({
    type: z.literal('EvidenceDownloaded'),
    evidence: evidenceRefSchema,
    attachment: attachmentRefSchema,
  }),

  // Case attachment
  z.object({
    type: z.literal('CaseAttachmentDownloaded'),
    attachment: attachmentRefSchema,
  }),

  // Report
  z.object({
    type: z.literal('ReportFieldChanged'),
    changes: z.array(reportFieldChangeSchema),
  }),
  z.object({ type: z.literal('ReportDownloaded') }),
  z.object({ type: z.literal('ReportPreviewOpened') }),
])

export type InvestigationAuditLogPayload = z.infer<typeof investigationAuditLogPayloadSchema>
export const investigationAuditLogTypeEnum = z.enum(
  investigationAuditLogPayloadSchema.options.map(o => o.shape.type.value)
).enum
